import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ArrowLeft, Plus, Trash2, Zap, Pencil, Loader2, ChevronDown, ChevronUp, Eye, Info, Copy
} from 'lucide-react';
import { toast } from 'sonner';

// ── Pole, operátory, akce ─────────────────────────────────────────────────────
// Záměrně jen to, co se při úpravách feedu opravdu používá (vzor: Mergado, Channable).

const FIELD_LABELS = {
  title: 'Název',
  description: 'Popis',
  brand: 'Značka',
  product_type: 'Kategorie z e-shopu',
  google_product_category: 'Kategorie Google',
  price: 'Cena',
  sale_price: 'Akční cena',
  discount_percent: 'Sleva v %',
  availability: 'Dostupnost',
  condition: 'Stav',
  color: 'Barva',
  size: 'Velikost',
  gender: 'Pohlaví',
  age_group: 'Věková skupina',
  original_id: 'ID produktu',
  item_group_id: 'ID skupiny variant',
  gtin: 'GTIN',
  mpn: 'MPN',
  image_count: 'Počet obrázků',
  custom_label_0: 'Custom label 0',
  custom_label_1: 'Custom label 1',
  custom_label_2: 'Custom label 2',
  custom_label_3: 'Custom label 3',
  custom_label_4: 'Custom label 4',
};

const CONDITION_FIELDS = [
  'title', 'description', 'brand', 'product_type', 'price', 'sale_price', 'discount_percent', 'availability',
  'color', 'size', 'original_id', 'item_group_id', 'image_count',
  'custom_label_0', 'custom_label_1', 'custom_label_2', 'custom_label_3', 'custom_label_4',
];

const WRITABLE_FIELDS = [
  'title', 'description', 'brand', 'product_type', 'google_product_category', 'availability', 'condition',
  'color', 'size', 'gender', 'age_group', 'gtin', 'mpn',
  'custom_label_0', 'custom_label_1', 'custom_label_2', 'custom_label_3', 'custom_label_4',
];
const TEXT_FIELDS = WRITABLE_FIELDS;

const OPERATORS = [
  { value: 'contains', label: 'obsahuje' },
  { value: 'not_contains', label: 'neobsahuje' },
  { value: 'equals', label: 'se rovná' },
  { value: 'not_equals', label: 'nerovná se' },
  { value: 'starts_with', label: 'začíná na' },
  { value: 'gt', label: 'větší než' },
  { value: 'lt', label: 'menší než' },
  { value: 'is_empty', label: 'je prázdné', noValue: true },
  { value: 'is_not_empty', label: 'není prázdné', noValue: true },
  { value: 'in_list', label: 'je v seznamu', list: true },
  { value: 'matches_regex', label: 'odpovídá regulárnímu výrazu' },
];
const operatorMeta = (value) => OPERATORS.find(o => o.value === value) || { label: value };

const ACTIONS = [
  { value: 'set_field', label: 'Nastavit hodnotu' },
  { value: 'replace', label: 'Najít a nahradit' },
  { value: 'adjust_price', label: 'Upravit cenu' },
  { value: 'truncate', label: 'Zkrátit text' },
  { value: 'strip_html', label: 'Odstranit HTML' },
  { value: 'deactivate', label: 'Vyřadit z exportu' },
  { value: 'set_main_image', label: 'Použít jinou fotku jako hlavní' },
  { value: 'set_overlay', label: 'Použít overlay na fotku' },
];

const TEMPLATE_HINT = 'Proměnné: {title} {brand} {color} {size} {price} {sale_price} {discount} — např. „{title} | {brand}"';

const selectCls = 'flex h-8 rounded-md border border-input bg-white px-2 text-xs';

// ── Tvar dat ──────────────────────────────────────────────────────────────────

const emptyCondition = () => ({ field: 'title', operator: 'contains', value: '' });
const emptyGroup = () => ({ logic: 'AND', conditions: [emptyCondition()] });
const emptyAction = () => ({ type: 'set_field', field: 'custom_label_0', value: '', replace_from: '', regex: false, mode: '', rounding: '' });

// Starší typy akcí převedené na současnou sadu, ať jde každé uložené pravidlo otevřít a upravit
function normalizeAction(a) {
  const base = { ...emptyAction(), ...Object.fromEntries(Object.entries(a).map(([k, v]) => [k, v ?? ''])) };
  const field = a.field === 'title_modified' ? 'title' : (a.field || '');
  switch (a.type) {
    case 'set_title_prefix': return { ...base, type: 'set_field', field: 'title', value: `${a.value || ''}{title}` };
    case 'set_title_suffix': return { ...base, type: 'set_field', field: 'title', value: `{title}${a.value || ''}` };
    case 'set_label': return { ...base, type: 'set_field', field };
    case 'prepend_text': return { ...base, type: 'set_field', field, value: `${a.value || ''}{${field}}` };
    case 'append_text': return { ...base, type: 'set_field', field, value: `{${field}}${a.value || ''}` };
    case 'replace_in_title': return { ...base, type: 'replace', field: 'title', regex: false };
    case 'replace_text': return { ...base, type: 'replace', field, regex: false };
    case 'regex_replace': return { ...base, type: 'replace', field, regex: true };
    default: return { ...base, field };
  }
}

function toForm(rule) {
  if (!rule) {
    return { name: '', order: 0, is_enabled: true, groups_logic: 'AND', condition_groups: [emptyGroup()], actions: [emptyAction()], output_ids: [] };
  }
  const groups = rule.condition_groups?.length
    ? rule.condition_groups
    : [{ logic: rule.conditions_logic || 'AND', conditions: rule.conditions || [] }];
  const actions = rule.actions?.length ? rule.actions : (rule.action ? [rule.action] : [emptyAction()]);
  return {
    name: rule.name || '',
    order: rule.order || 0,
    is_enabled: rule.is_enabled !== false,
    groups_logic: rule.groups_logic || 'AND',
    condition_groups: groups.map(g => ({
      logic: g.logic || 'AND',
      conditions: (g.conditions || []).map(c => ({ field: c.field === 'title_original' || c.field === 'title_modified' ? 'title' : c.field, operator: c.operator, value: c.value ?? '' })),
    })),
    actions: actions.map(normalizeAction),
    output_ids: rule.output_ids || [],
  };
}

function toPayload(form) {
  const clean = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === '' ? null : v]));
  return {
    name: form.name.trim(),
    apply_on: 'publish',
    order: Number(form.order) || 0,
    is_enabled: form.is_enabled,
    groups_logic: form.groups_logic,
    condition_groups: form.condition_groups
      .map(g => ({ logic: g.logic, conditions: g.conditions.map(c => clean(c)) }))
      .filter(g => g.conditions.length > 0),
    actions: form.actions.map(({ regex, ...a }) => clean(
      a.type === 'replace' ? { ...a, type: regex ? 'regex_replace' : 'replace_text' } : a
    )),
    output_ids: form.output_ids,
  };
}

function describeCondition(c) {
  const meta = operatorMeta(c.operator);
  const field = FIELD_LABELS[c.field] || c.field;
  if (meta.noValue) return `${field} ${meta.label}`;
  return `${field} ${meta.label} „${c.value}"`;
}

function describeAction(raw) {
  const a = normalizeAction(raw);
  const field = FIELD_LABELS[a.field] || a.field;
  switch (a.type) {
    case 'set_field': return `${field} = „${a.value}"`;
    case 'replace': return `${field}: „${a.replace_from}" → „${a.value}"${a.regex ? ' (regex)' : ''}`;
    case 'adjust_price': {
      const unit = a.mode === 'amount' || a.mode === 'set' ? ' Kč' : ' %';
      const sign = a.mode === 'set' ? '= ' : (Number(a.value) >= 0 ? '+' : '');
      return `${field} ${sign}${a.value}${unit}${a.rounding && a.rounding !== 'none' ? ', zaokrouhlit' : ''}`;
    }
    case 'truncate': return `${field}: zkrátit na ${a.value} znaků`;
    case 'strip_html': return `${field}: bez HTML`;
    case 'deactivate': return 'vyřadit z exportu';
    case 'set_main_image': return `hlavní fotka = ${a.value}. obrázek`;
    case 'set_overlay': return 'overlay na fotku';
    default: return a.type;
  }
}

// ── Editor podmínky a akce ────────────────────────────────────────────────────

function FieldSelect({ value, onChange, fields, className = '' }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className={`${selectCls} ${className}`}>
      <option value="">— pole —</option>
      {fields.map(f => <option key={f} value={f}>{FIELD_LABELS[f] || f}</option>)}
    </select>
  );
}

function ConditionRow({ condition, onChange, onRemove }) {
  const meta = operatorMeta(condition.operator);
  return (
    <div className="flex flex-wrap items-start gap-2 p-2 bg-white rounded-md border border-slate-200">
      <select value={condition.field} onChange={e => onChange({ field: e.target.value })} className={`${selectCls} w-44`}>
        {!CONDITION_FIELDS.includes(condition.field) && <option value={condition.field}>{condition.field}</option>}
        {CONDITION_FIELDS.map(f => <option key={f} value={f}>{FIELD_LABELS[f] || f}</option>)}
      </select>
      <select value={condition.operator} onChange={e => onChange({ operator: e.target.value })} className={`${selectCls} w-48`}>
        {!OPERATORS.some(op => op.value === condition.operator) && <option value={condition.operator}>{condition.operator}</option>}
        {OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
      </select>
      {meta.list ? (
        <textarea value={condition.value} onChange={e => onChange({ value: e.target.value })}
          placeholder="hodnoty oddělené čárkou nebo řádkem" rows={2}
          className="flex-1 min-w-[160px] rounded-md border border-input bg-white px-2 py-1 text-xs" />
      ) : !meta.noValue ? (
        <Input value={condition.value} onChange={e => onChange({ value: e.target.value })}
          placeholder={condition.operator.includes('regex') ? 'regulární výraz' : 'hodnota'}
          className="h-8 text-xs flex-1 min-w-[140px]" />
      ) : <div className="flex-1" />}
      <button type="button" onClick={onRemove} className="p-1.5 text-slate-400 hover:text-red-500" title="Odebrat podmínku">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function ActionRow({ action, index, onChange, onRemove, overlayTemplates, canRemove }) {
  const known = ACTIONS.some(a => a.value === action.type);
  const changeType = (type) => onChange({
    ...emptyAction(), type,
    field: type === 'adjust_price' ? 'price' : type === 'set_field' ? 'custom_label_0' : 'title',
    mode: type === 'adjust_price' ? 'percent' : '', rounding: type === 'adjust_price' ? 'none' : '',
  });
  const set = (patch) => onChange({ ...action, ...patch });

  return (
    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2" data-testid={`action-${index}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono text-slate-400 w-10">{index === 0 ? 'PAK' : 'A PAK'}</span>
        <select value={known ? action.type : ''} onChange={e => changeType(e.target.value)} className={`${selectCls} flex-1 h-9 text-sm`} data-testid={`action-type-${index}`}>
          {!known && <option value="">starší akce „{action.type}" — vyber náhradu</option>}
          {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        {canRemove && (
          <button type="button" onClick={onRemove} className="p-1.5 text-slate-400 hover:text-red-500" title="Odebrat akci">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 pl-12">
        {['set_field', 'replace', 'truncate', 'strip_html'].includes(action.type) && (
          <FieldSelect value={action.field} onChange={v => set({ field: v })} fields={action.type === 'set_field' ? WRITABLE_FIELDS : TEXT_FIELDS} className="w-44" />
        )}
        {action.type === 'set_field' && (
          <Input value={action.value} onChange={e => set({ value: e.target.value })} placeholder="hodnota, např. {title} | {brand}"
            className="h-8 text-xs flex-1 min-w-[200px]" data-testid={`action-value-${index}`} />
        )}
        {action.type === 'replace' && (
          <>
            <Input value={action.replace_from} onChange={e => set({ replace_from: e.target.value })} placeholder="najít"
              className="h-8 text-xs flex-1 min-w-[140px]" />
            <Input value={action.value} onChange={e => set({ value: e.target.value })} placeholder="nahradit čím (může být prázdné)"
              className="h-8 text-xs flex-1 min-w-[140px]" data-testid={`action-value-${index}`} />
            <label className="flex items-center gap-1 text-xs text-slate-600">
              <input type="checkbox" checked={!!action.regex} onChange={e => set({ regex: e.target.checked })} /> regulární výraz
            </label>
          </>
        )}
        {action.type === 'adjust_price' && (
          <>
            <select value={action.field} onChange={e => set({ field: e.target.value })} className={`${selectCls} w-32`}>
              <option value="price">Cena</option>
              <option value="sale_price">Akční cena</option>
            </select>
            <select value={action.mode || 'percent'} onChange={e => set({ mode: e.target.value })} className={`${selectCls} w-36`}>
              <option value="percent">změnit o %</option>
              <option value="amount">změnit o částku</option>
              <option value="set">nastavit na</option>
            </select>
            <Input type="number" value={action.value} onChange={e => set({ value: e.target.value })} placeholder="např. -10" className="h-8 text-xs w-24" />
            <select value={action.rounding || 'none'} onChange={e => set({ rounding: e.target.value })} className={`${selectCls} w-44`}>
              <option value="none">bez zaokrouhlení</option>
              <option value="whole">na celé koruny</option>
              <option value="end_9">nahoru na …9</option>
              <option value="end_90">nahoru na …90</option>
            </select>
          </>
        )}
        {action.type === 'truncate' && (
          <Input type="number" value={action.value} onChange={e => set({ value: e.target.value })} placeholder="počet znaků" className="h-8 text-xs w-32" />
        )}
        {action.type === 'set_main_image' && (
          <>
            <span className="text-xs text-slate-500">pořadí fotky</span>
            <Input type="number" min="1" value={action.value} onChange={e => set({ value: e.target.value })} placeholder="2" className="h-8 text-xs w-20" />
          </>
        )}
        {action.type === 'set_overlay' && (
          <select value={action.value} onChange={e => set({ value: e.target.value })} className={`${selectCls} w-56`} data-testid="overlay-template-select">
            <option value="">— vyber šablonu —</option>
            {overlayTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>
      {action.type === 'set_field' && <p className="text-[11px] text-slate-400 pl-12">{TEMPLATE_HINT}</p>}
    </div>
  );
}

// ── Formulář pravidla ─────────────────────────────────────────────────────────

function RuleForm({ projectId, initial, onSave, onCancel, saving, overlayTemplates = [], outputs = [] }) {
  const [form, setForm] = useState(() => toForm(initial));
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const setGroup = (gi, patch) => setForm(f => ({
    ...f, condition_groups: f.condition_groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)),
  }));
  const setCondition = (gi, ci, patch) => setGroup(gi, {
    conditions: form.condition_groups[gi].conditions.map((c, i) => (i === ci ? { ...c, ...patch } : c)),
  });
  const addCondition = (gi) => setGroup(gi, { conditions: [...form.condition_groups[gi].conditions, emptyCondition()] });
  const removeCondition = (gi, ci) => setForm(f => ({
    ...f,
    condition_groups: f.condition_groups
      .map((g, i) => (i === gi ? { ...g, conditions: g.conditions.filter((_, j) => j !== ci) } : g))
      .filter((g, i) => g.conditions.length > 0 || f.condition_groups.length === 1 || i === 0),
  }));
  const addGroup = () => setForm(f => ({ ...f, condition_groups: [...f.condition_groups, emptyGroup()] }));
  const removeGroup = (gi) => setForm(f => ({ ...f, condition_groups: f.condition_groups.filter((_, i) => i !== gi) }));

  const setAction = (ai, next) => setForm(f => ({ ...f, actions: f.actions.map((a, i) => (i === ai ? next : a)) }));
  const addAction = () => setForm(f => ({ ...f, actions: [...f.actions, emptyAction()] }));
  const removeAction = (ai) => setForm(f => ({ ...f, actions: f.actions.filter((_, i) => i !== ai) }));

  const noConditions = form.condition_groups.every(g => g.conditions.length === 0);

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await api.post(`/projects/${projectId}/rules/preview-draft`, {
        rule: { ...toPayload(form), name: form.name.trim() || 'náhled' }, limit: 8,
      });
      setPreview(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(d => d.msg.replace('Value error, ', '')).join('; ') : (detail || 'Náhled se nepodařil'));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <Label>Název pravidla</Label>
        <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="např. Značka na konec názvu" maxLength={100} data-testid="rule-name" />
      </div>

      {outputs.length > 1 && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="rule-outputs">
          <div className="text-sm font-medium">Platí pro kanály</div>
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" checked={form.output_ids.length === 0} onChange={() => setForm(f => ({ ...f, output_ids: [] }))} />
              všechny
            </label>
            {outputs.map(o => (
              <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.output_ids.includes(o.id)}
                  onChange={e => setForm(f => ({
                    ...f,
                    output_ids: e.target.checked ? [...f.output_ids, o.id] : f.output_ids.filter(id => id !== o.id),
                  }))} />
                {o.name}
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-500">Náhled níže ukazuje změny bez ohledu na výstup.</p>
        </div>
      )}

      {/* KDYŽ */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>KDYŽ</Label>
          {form.condition_groups.length > 1 && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              mezi skupinami:
              {['AND', 'OR'].map(l => (
                <button key={l} type="button" onClick={() => setForm(f => ({ ...f, groups_logic: l }))}
                  className={`px-2 py-0.5 rounded border font-mono ${form.groups_logic === l ? 'bg-amber-500 border-amber-500 text-slate-900' : 'border-slate-300'}`}>
                  {l === 'AND' ? 'A ZÁROVEŇ' : 'NEBO'}
                </button>
              ))}
            </div>
          )}
        </div>

        {form.condition_groups.map((group, gi) => (
          <div key={gi} className="space-y-2">
            {gi > 0 && (
              <div className="text-center text-xs font-mono text-amber-700">{form.groups_logic === 'AND' ? 'A ZÁROVEŇ' : 'NEBO'}</div>
            )}
            <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 space-y-2" data-testid={`group-${gi}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  {group.conditions.length > 1 && (
                    <>
                      platí
                      {['AND', 'OR'].map(l => (
                        <button key={l} type="button" onClick={() => setGroup(gi, { logic: l })}
                          className={`px-2 py-0.5 rounded border ${group.logic === l ? 'bg-amber-500 border-amber-500 text-slate-900' : 'border-slate-300'}`}>
                          {l === 'AND' ? 'všechny' : 'aspoň jedna'}
                        </button>
                      ))}
                    </>
                  )}
                </div>
                {form.condition_groups.length > 1 && (
                  <button type="button" onClick={() => removeGroup(gi)} className="text-xs text-slate-400 hover:text-red-500">odebrat skupinu</button>
                )}
              </div>
              {group.conditions.map((condition, ci) => (
                <ConditionRow key={ci} condition={condition}
                  onChange={patch => setCondition(gi, ci, patch)} onRemove={() => removeCondition(gi, ci)} />
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => addCondition(gi)} className="text-xs h-7" data-testid={`add-condition-${gi}`}>
                <Plus className="w-3 h-3 mr-1" /> podmínka
              </Button>
            </div>
          </div>
        ))}
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={addGroup} className="text-xs h-7" data-testid="add-group">
            <Plus className="w-3 h-3 mr-1" /> skupina podmínek
          </Button>
          {noConditions && <span className="text-xs text-amber-700">Bez podmínek platí pravidlo pro všechny produkty.</span>}
        </div>
      </div>

      {/* PAK */}
      <div className="space-y-2">
        <Label>PAK</Label>
        {form.actions.map((action, ai) => (
          <ActionRow key={ai} index={ai} action={action} overlayTemplates={overlayTemplates}
            onChange={next => setAction(ai, next)} onRemove={() => removeAction(ai)} canRemove={form.actions.length > 1} />
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addAction} className="text-xs h-7" data-testid="add-action">
          <Plus className="w-3 h-3 mr-1" /> další akce
        </Button>
      </div>

      {/* Náhled */}
      <div className="rounded-lg border border-slate-200 p-3 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-slate-600">
            {preview
              ? <>Podmínky splní <strong>{preview.matched}</strong> z {preview.total_products} produktů, změní se <strong>{preview.changed}</strong>.</>
              : 'Před uložením si ověř, na co pravidlo dopadne.'}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={handlePreview} disabled={previewing} data-testid="draft-preview-btn">
            {previewing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}
            Náhled
          </Button>
        </div>
        {preview?.samples?.length > 0 && (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {preview.samples.map(sample => (
              <div key={sample.product_id} className="text-xs bg-slate-50 rounded p-2 border border-slate-100">
                <div className="text-slate-500 truncate"><span className="font-mono">{sample.original_id}</span> · {sample.title}</div>
                {Object.entries(sample.changes).map(([field, change]) => (
                  <div key={field} className="mt-1 grid grid-cols-[110px_1fr] gap-2">
                    <span className="text-slate-400 truncate">{FIELD_LABELS[field === 'title_modified' ? 'title' : field] || field}</span>
                    <span>
                      <span className="line-through text-red-400 break-words">{String(change.before ?? '—').slice(0, 140)}</span>
                      <span className="text-slate-400"> → </span>
                      <span className="text-green-700 font-medium break-words">{String(change.after ?? '—').slice(0, 160)}</span>
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-200">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500">Pořadí</Label>
          <Input type="number" min={0} max={9999} value={form.order}
            onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value, 10) || 0 }))} className="h-8 text-xs w-20" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>Zrušit</Button>
          <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={() => onSave(toPayload(form))}
            disabled={saving || !form.name.trim()} data-testid="save-rule-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Uložit pravidlo
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Karta pravidla ────────────────────────────────────────────────────────────

function RuleCard({ rule, outputs = [], onEdit, onDuplicate, onDelete, onToggle, deleting }) {
  const [expanded, setExpanded] = useState(false);
  const form = toForm(rule);
  const groups = form.condition_groups.filter(g => g.conditions.length > 0);
  const conditionText = groups.length === 0
    ? 'všechny produkty'
    : groups.map(g => {
      const inner = g.conditions.map(describeCondition).join(g.logic === 'AND' ? ' a ' : ' nebo ');
      return groups.length > 1 ? `(${inner})` : inner;
    }).join(form.groups_logic === 'AND' ? ' a zároveň ' : ' nebo ');
  const actionText = form.actions.map(describeAction).join('; ');

  return (
    <Card className={`border ${rule.is_enabled ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-slate-900">{rule.name}</span>
              <Badge variant="outline" className="text-xs font-mono">#{rule.order}</Badge>
              {form.actions.length > 1 && <Badge variant="outline" className="text-xs">{form.actions.length} akce</Badge>}
              {(rule.output_ids || []).length > 0 && (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                  jen {(rule.output_ids || []).map(id => outputs.find(o => o.id === id)?.name || 'smazaný kanál').join(', ')}
                </Badge>
              )}
              {!rule.is_enabled && <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">vypnuto</Badge>}
            </div>
            {!expanded && (
              <p className="text-xs text-slate-500 mt-1 truncate">KDYŽ {conditionText} → {actionText}</p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Switch checked={rule.is_enabled} onCheckedChange={onToggle} className="scale-90" />
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit} title="Upravit"><Pencil className="w-3.5 h-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDuplicate} title="Duplikovat"><Copy className="w-3.5 h-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={onDelete} disabled={deleting} title="Smazat">
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
        {expanded && (
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 text-xs text-slate-600">
            <div>
              <span className="font-medium text-slate-700">KDYŽ</span>
              {groups.length === 0 ? <span className="ml-2">všechny produkty</span> : groups.map((g, gi) => (
                <div key={gi} className="ml-3 mt-1">
                  {gi > 0 && <div className="text-amber-700 font-mono">{form.groups_logic === 'AND' ? 'A ZÁROVEŇ' : 'NEBO'}</div>}
                  <ul className="space-y-0.5">
                    {g.conditions.map((c, ci) => (
                      <li key={ci}>{ci > 0 && <span className="text-amber-700 mr-1">{g.logic === 'AND' ? 'a' : 'nebo'}</span>}{describeCondition(c)}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div>
              <span className="font-medium text-slate-700">PAK</span>
              <ul className="ml-3 mt-1 space-y-0.5">
                {form.actions.map((a, ai) => <li key={ai}>{describeAction(a)}</li>)}
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Stránka ───────────────────────────────────────────────────────────────────

export default function RulesPage() {
  const { projectId } = useParams();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [draftFrom, setDraftFrom] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [overlayTemplates, setOverlayTemplates] = useState([]);
  const [outputs, setOutputs] = useState([]);

  const fetchRules = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/rules`);
      setRules(res.data);
    } catch {
      toast.error('Nepodařilo se načíst pravidla');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchRules();
    api.get(`/projects/${projectId}/overlay-templates`).then(res => setOverlayTemplates(res.data)).catch(() => {});
    api.get(`/projects/${projectId}/outputs`).then(res => setOutputs(res.data)).catch(() => {});
  }, [fetchRules, projectId]);

  const closeForm = () => { setShowForm(false); setEditRule(null); setDraftFrom(null); };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editRule) {
        await api.put(`/projects/${projectId}/rules/${editRule.id}`, payload);
        toast.success('Pravidlo uloženo');
      } else {
        await api.post(`/projects/${projectId}/rules`, payload);
        toast.success('Pravidlo vytvořeno');
      }
      closeForm();
      fetchRules();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(d => d.msg.replace('Value error, ', '')).join('; ') : (detail || 'Chyba při ukládání'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (rule) => {
    try {
      await api.put(`/projects/${projectId}/rules/${rule.id}`, { is_enabled: !rule.is_enabled });
      setRules(prev => prev.map(r => (r.id === rule.id ? { ...r, is_enabled: !r.is_enabled } : r)));
    } catch {
      toast.error('Nepodařilo se změnit stav pravidla');
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/projects/${projectId}/rules/${id}`);
      setRules(prev => prev.filter(r => r.id !== id));
      toast.success('Pravidlo smazáno');
    } catch {
      toast.error('Nepodařilo se smazat pravidlo');
    } finally {
      setDeletingId(null);
    }
  };

  const handlePreviewAll = async () => {
    setLoadingPreview(true);
    setShowPreview(true);
    try {
      const res = await api.post(`/projects/${projectId}/rules/preview`);
      setPreviewResult(res.data);
    } catch {
      toast.error('Nepodařilo se načíst náhled pravidel');
      setShowPreview(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }

  const enabledCount = rules.filter(r => r.is_enabled).length;
  const formInitial = editRule || (draftFrom ? { ...draftFrom, name: `${draftFrom.name} (kopie)` } : null);

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center gap-4 mb-6">
        <Button asChild variant="ghost" size="icon">
          <Link to={`/projects/${projectId}`}><ArrowLeft className="w-5 h-5" /></Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold text-slate-900">Pravidla</h1>
          <p className="text-sm text-slate-500">Úpravy exportovaných dat. KDYŽ produkt splní podmínky → PAK se provedou akce, v pořadí podle čísla.</p>
        </div>
        <div className="flex items-center gap-2">
          {rules.length > 0 && (
            <Button variant="outline" onClick={handlePreviewAll} disabled={loadingPreview}>
              {loadingPreview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              Dopad všech pravidel
            </Button>
          )}
          <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={() => { setEditRule(null); setDraftFrom(null); setShowForm(true); }} data-testid="new-rule-btn">
            <Plus className="w-4 h-4 mr-2" /> Nové pravidlo
          </Button>
        </div>
      </div>

      {rules.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Zap className="w-12 h-12 text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">Žádná pravidla</h3>
          <p className="text-slate-500 mb-6 max-w-sm">Třeba: doplnit značku do názvu, zkrátit názvy pro Google, nastavit custom label zboží se slevou nad 30 % nebo vyřadit vyprodané.</p>
          <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" /> Vytvořit první pravidlo
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-slate-400">{rules.length} pravidel, {enabledCount} zapnutých</div>
          {rules.map(rule => (
            <RuleCard key={rule.id} rule={rule} outputs={outputs}
              onEdit={() => { setDraftFrom(null); setEditRule(rule); setShowForm(true); }}
              onDuplicate={() => { setEditRule(null); setDraftFrom(rule); setShowForm(true); }}
              onDelete={() => handleDelete(rule.id)}
              onToggle={() => handleToggleEnabled(rule)}
              deleting={deletingId === rule.id} />
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={open => { if (!open) closeForm(); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editRule ? 'Upravit pravidlo' : 'Nové pravidlo'}</DialogTitle>
            <DialogDescription>Podmínky, akce a náhled dopadu na skutečných produktech.</DialogDescription>
          </DialogHeader>
          {showForm && (
            <RuleForm key={editRule?.id || draftFrom?.id || 'new'} projectId={projectId} initial={formInitial}
              overlayTemplates={overlayTemplates} outputs={outputs} onSave={handleSave} onCancel={closeForm} saving={saving} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Dopad všech zapnutých pravidel</DialogTitle>
            <DialogDescription>Kolik produktů pravidla změní a ukázka změn</DialogDescription>
          </DialogHeader>
          {loadingPreview ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
          ) : previewResult ? (
            <div className="space-y-4">
              <div className="flex items-center gap-6 p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm">
                <div><p className="text-xl font-bold">{previewResult.total_products}</p><p className="text-xs text-slate-500">produktů</p></div>
                <div><p className="text-xl font-bold text-amber-600">{previewResult.affected_count}</p><p className="text-xs text-slate-500">se změní</p></div>
                <div><p className="text-xl font-bold text-blue-600">{previewResult.total_rules}</p><p className="text-xs text-slate-500">zapnutých pravidel</p></div>
              </div>
              {previewResult.sample_diffs?.map((diff, i) => (
                <div key={i} className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs">
                  <p className="text-slate-500 mb-1 truncate">{diff.title}</p>
                  {Object.entries(diff.changes).filter(([f]) => f !== 'updated_at').map(([field, change]) => (
                    <div key={field} className="flex items-center gap-1 mt-1">
                      <span className="text-slate-400 shrink-0">{FIELD_LABELS[field === 'title_modified' ? 'title' : field] || field}:</span>
                      <span className="line-through text-red-400 truncate max-w-40">{String(change.before ?? '—')}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-green-600 font-medium truncate max-w-48">{String(change.after ?? '—')}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Zavřít</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
